import { useEffect, useState } from "react"
import {
  BriefcaseIcon,
  MagnifyingGlassIcon,
  ExclamationTriangleIcon,
} from "@heroicons/react/24/outline"
import { Modal } from "../../../components/modal/Modal"
import {
  AvailableCourseVacancyItem,
  listAvailableCourseVacanciesApi,
  saveCourseVacanciesApi,
} from "../api/courseVacancies.api"

type Props = {
  open: boolean
  onClose: () => void
  courseId: string
  courseTitle?: string
  onSaved?: (selectedCount: number) => void
  onForbidden?: () => void
}

const formatSalary = (v: AvailableCourseVacancyItem) => {
  if (!v.salary_to) return null
  return `до ${v.salary_to.toLocaleString()} ${v.salary_currency ?? ""}`.trim()
}

export const SelectVacanciesModal = ({
  open,
  onClose,
  courseId,
  courseTitle,
  onSaved,
  onForbidden,
}: Props) => {
  const [search, setSearch] = useState("")
  const [debouncedSearch, setDebouncedSearch] = useState("")
  const [items, setItems] = useState<AvailableCourseVacancyItem[]>([])
  const [total, setTotal] = useState(0)
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [forbidden, setForbidden] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // reset on open
  useEffect(() => {
    if (!open) return
    setSearch("")
    setDebouncedSearch("")
    setItems([])
    setTotal(0)
    setSelectedIds(new Set())
    setForbidden(false)
    setError(null)
  }, [open, courseId])

  // debounce search input
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 350)
    return () => clearTimeout(t)
  }, [search])

  useEffect(() => {
    if (!open) return

    let cancelled = false
    setLoading(true)
    setError(null)

    listAvailableCourseVacanciesApi(courseId, { search: debouncedSearch || undefined })
      .then((res) => {
        if (cancelled) return
        setItems(res.data.items)
        setTotal(res.data.total)
        setSelectedIds((prev) => {
          const next = new Set(prev)
          res.data.items.forEach((v) => {
            if (v.is_selected) next.add(v.id)
          })
          return next
        })
      })
      .catch((err) => {
        if (cancelled) return
        if (err?.response?.status === 403) {
          setForbidden(true)
        } else {
          setError("Не удалось загрузить вакансии. Попробуйте позже.")
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [open, courseId, debouncedSearch])

  const toggle = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const handleSave = async () => {
    setSaving(true)
    setError(null)
    try {
      await saveCourseVacanciesApi(courseId, Array.from(selectedIds))
      onSaved?.(selectedIds.size)
      onClose()
    } catch (err: any) {
      if (err?.response?.status === 403) {
        setForbidden(true)
      } else {
        setError("Не удалось сохранить выбор. Попробуйте позже.")
      }
    } finally {
      setSaving(false)
    }
  }

  const handleForbiddenBack = () => {
    onForbidden?.()
    onClose()
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={courseTitle ? `Вакансии курса «${courseTitle}»` : "Вакансии курса"}
    >
      {/* ── 403 forbidden ── */}
      {forbidden ? (
        <div className="py-6 text-center space-y-3">
          <div className="w-12 h-12 rounded-2xl bg-amber-50 flex items-center justify-center mx-auto">
            <ExclamationTriangleIcon className="w-6 h-6 text-amber-500" />
          </div>
          <div>
            <h3 className="font-semibold text-gray-900 text-sm">Нет доступа</h3>
            <p className="text-sm text-gray-500 mt-1">
              Вы не являетесь ментором этого курса, поэтому не можете управлять его вакансиями.
            </p>
          </div>
          <button
            onClick={handleForbiddenBack}
            className="mt-2 px-4 py-2 text-sm font-medium rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-700 transition-colors"
          >
            Назад к курсам
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {/* Search */}
          <div className="relative">
            <MagnifyingGlassIcon className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Поиск вакансий по названию…"
              className="w-full pl-9 pr-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Error */}
          {error && (
            <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
              {error}
            </div>
          )}

          {/* List */}
          <div className="max-h-80 overflow-y-auto -mx-1 px-1 space-y-2">
            {loading ? (
              Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="h-14 bg-gray-100 rounded-lg animate-pulse" />
              ))
            ) : items.length === 0 ? (
              <div className="py-10 text-center">
                <BriefcaseIcon className="w-8 h-8 text-gray-200 mx-auto mb-2" />
                <p className="text-sm text-gray-400">
                  {total === 0 && !debouncedSearch
                    ? "В системе пока нет вакансий"
                    : `Ничего не найдено по запросу «${debouncedSearch}»`}
                </p>
              </div>
            ) : (
              items.map((v) => (
                <label
                  key={v.id}
                  className="flex items-start gap-3 px-3 py-2.5 rounded-lg border border-gray-100 hover:border-blue-200 hover:bg-blue-50/30 cursor-pointer transition-colors"
                >
                  <input
                    type="checkbox"
                    checked={selectedIds.has(v.id)}
                    onChange={() => toggle(v.id)}
                    className="mt-0.5 w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 flex-shrink-0"
                  />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-gray-800 truncate">{v.title}</p>
                    <div className="flex flex-wrap items-center gap-1.5 mt-1">
                      {formatSalary(v) && (
                        <span className="text-xs text-gray-500 bg-gray-50 border border-gray-100 rounded px-1.5 py-0.5">
                          {formatSalary(v)}
                        </span>
                      )}
                      {v.employment_type && (
                        <span className="text-xs text-gray-500 bg-gray-50 border border-gray-100 rounded px-1.5 py-0.5 capitalize">
                          {v.employment_type}
                        </span>
                      )}
                      {v.work_format && (
                        <span className="text-xs text-gray-500 bg-gray-50 border border-gray-100 rounded px-1.5 py-0.5 capitalize">
                          {v.work_format}
                        </span>
                      )}
                    </div>
                  </div>
                </label>
              ))
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between pt-2 border-t">
            <span className="text-xs text-gray-400">
              Выбрано: <span className="font-medium text-gray-600">{selectedIds.size}</span>
            </span>
            <div className="flex gap-2">
              <button
                onClick={onClose}
                disabled={saving}
                className="px-4 py-2 border rounded-lg text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50 transition-colors"
              >
                Отмена
              </button>
              <button
                onClick={handleSave}
                disabled={saving || loading}
                className="px-4 py-2 rounded-lg text-sm text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {saving ? "Сохранение…" : "Сохранить"}
              </button>
            </div>
          </div>
        </div>
      )}
    </Modal>
  )
}